import React, { useState } from 'react';
import { Card, Table, Form, Badge, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import moment from 'moment';
import { toast } from 'react-toastify';
import { BANGDIEM_LIENTUC_API, FILE_MANAGER_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { Get, Post, PostFormData } from '../../../../utils/httpProvider';
import ImportBangDiemLienTucModal from './ImportBangDiemLienTucModal';

const XEP_LOAI_OPTIONS = [
    { value: '', label: '-- Chọn xếp loại --' },
    { value: 'Xuất sắc', label: 'Xuất sắc' },
    { value: 'Giỏi', label: 'Giỏi' },
    { value: 'Khá', label: 'Khá' },
    { value: 'Trung bình', label: 'Trung bình' },
    { value: 'Yếu', label: 'Yếu' },
    { value: 'Kém', label: 'Kém' }
];

const BangDiemLienTucTab = ({ lopId, lopDetail }) => {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [fileMinhChungPath, setFileMinhChungPath] = useState('');
    const [fileMinhChungName, setFileMinhChungName] = useState('');
    const [items, setItems] = useState([]);
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [mobileIndex, setMobileIndex] = useState(0);

    // 1. Fetch Bảng điểm của Lớp
    const { data: bangDiemData, isLoading, refetch } = useQuery(
        ['bangDiemLienTuc', lopId],
        () => Get(`${BANGDIEM_LIENTUC_API.GET_BY_LOP}/${lopId}`),
        {
            enabled: !!lopId,
            staleTime: 0,
            cacheTime: 0,
            onSuccess: (res) => {
                setFileMinhChungPath(res?.fileMinhChungPath || '');
                setFileMinhChungName(res?.fileMinhChungName || '');
                const rawList = res?.danhSachBangDiem?.$values || res?.danhSachBangDiem || [];
                setItems(rawList.map(item => ({
                    ...item,
                    diemLyThuyet: item.diemLyThuyet !== null && item.diemLyThuyet !== undefined ? item.diemLyThuyet : '',
                    diemThucHanh: item.diemThucHanh !== null && item.diemThucHanh !== undefined ? item.diemThucHanh : '',
                    diemTrungBinh: item.diemTrungBinh !== null && item.diemTrungBinh !== undefined ? item.diemTrungBinh : '',
                    xepLoai: item.xepLoai || '',
                    isDat: item.isDat !== null && item.isDat !== undefined ? item.isDat : null,
                    nhanXet: item.nhanXet || ''
                })));
            }
        }
    );

    // 2. Khởi tạo / Đồng bộ danh sách học viên
    const syncMutation = useMutation(
        () => Post(`${BANGDIEM_LIENTUC_API.KHOI_TAO}/${lopId}`, null, {}),
        {
            onSuccess: () => {
                toast.success('Đã đồng bộ danh sách học viên vào bảng điểm!');
                refetch();
            },
            onError: (err) => {
                toast.error(err?.response?.data?.message || 'Lỗi khi đồng bộ danh sách học viên!');
            }
        }
    );

    // 3. Lưu toàn bộ bảng điểm
    const saveMutation = useMutation(
        (payload) => Post(BANGDIEM_LIENTUC_API.SAVE_BATCH, null, payload),
        {
            onSuccess: () => {
                toast.success('Lưu bảng điểm thành công!');
                refetch();
            },
            onError: (err) => {
                toast.error(err?.response?.data?.message || 'Lỗi khi lưu bảng điểm!');
            }
        }
    );

    // Xử lý upload file minh chứng chung của lớp
    const handleFileUpload = async (e) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setIsUploadingFile(true);
            try {
                const formData = new FormData();
                formData.append('file', selectedFile);
                const res = await PostFormData(FILE_MANAGER_API.UPLOAD, formData);
                const uploaded = Array.isArray(res) ? res[0] : res;
                const fileUrl = uploaded?.filePath || uploaded?.FilePath || uploaded?.path || uploaded?.fileUrl || res?.path || res?.fileUrl;

                if (fileUrl) {
                    setFileMinhChungPath(fileUrl);
                    setFileMinhChungName(selectedFile.name);
                    toast.success(`Đã tải lên file minh chứng: ${selectedFile.name}`);
                } else {
                    toast.error('Không nhận được đường dẫn file từ máy chủ.');
                }
            } catch (error) {
                toast.error('Lỗi khi tải file lên máy chủ!');
            } finally {
                setIsUploadingFile(false);
            }
        }
    };

    const handleRemoveFile = () => {
        if (window.confirm('Bạn có chắc chắn muốn xóa file minh chứng này không?')) {
            setFileMinhChungPath('');
            setFileMinhChungName('');
            toast.info('Đã xóa file đính kèm (hãy bấm "Lưu bảng điểm" để cập nhật).');
        }
    };

    // Xử lý thay đổi dữ liệu từng ô
    const handleFieldChange = (index, field, value) => {
        setItems(prev => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                [field]: value
            };
            return next;
        });
    };

    // Gợi ý tính điểm trung bình (LT + TH) / 2
    const handleAutoCalculateRow = (index) => {
        setItems(prev => {
            const next = [...prev];
            const row = next[index];
            const lt = parseFloat(row.diemLyThuyet);
            const th = parseFloat(row.diemThucHanh);

            if (!isNaN(lt) && !isNaN(th)) {
                const tb = Math.round(((lt + th) / 2) * 100) / 100;
                row.diemTrungBinh = tb;

                // Gợi ý Xếp loại
                if (tb >= 9.0) row.xepLoai = 'Xuất sắc';
                else if (tb >= 8.0) row.xepLoai = 'Giỏi';
                else if (tb >= 7.0) row.xepLoai = 'Khá';
                else if (tb >= 5.0) row.xepLoai = 'Trung bình';
                else row.xepLoai = 'Yếu';

                // Gợi ý Đạt/KĐ
                if (lt >= 5.0 && th >= 5.0 && tb >= 5.0) {
                    row.isDat = true;
                } else {
                    row.isDat = false;
                }
            } else {
                toast.warning('Cần có đủ Điểm Lý thuyết và Điểm Thực hành để tính tự động!');
            }
            return next;
        });
    };

    // Áp dụng dữ liệu từ Modal Import Excel
    const handleApplyImportedData = (importedList) => {
        setItems(prev => {
            const next = [...prev];
            importedList.forEach(imp => {
                const rowIdx = next.findIndex(x => x.hoSoTuyenSinhId === imp.hoSoTuyenSinhId);
                if (rowIdx !== -1) {
                    next[rowIdx] = {
                        ...next[rowIdx],
                        diemLyThuyet: imp.diemLyThuyet !== null && imp.diemLyThuyet !== undefined ? imp.diemLyThuyet : next[rowIdx].diemLyThuyet,
                        diemThucHanh: imp.diemThucHanh !== null && imp.diemThucHanh !== undefined ? imp.diemThucHanh : next[rowIdx].diemThucHanh,
                        diemTrungBinh: imp.diemTrungBinh !== null && imp.diemTrungBinh !== undefined ? imp.diemTrungBinh : next[rowIdx].diemTrungBinh,
                        xepLoai: imp.xepLoai || next[rowIdx].xepLoai,
                        isDat: imp.isDat !== null && imp.isDat !== undefined ? imp.isDat : next[rowIdx].isDat,
                        nhanXet: imp.nhanXet || next[rowIdx].nhanXet
                    };
                }
            });
            return next;
        });
    };

    // Lưu toàn bộ bảng điểm
    const handleSaveAll = () => {
        const payload = {
            lopId,
            fileMinhChungPath,
            fileMinhChungName,
            danhSachBangDiem: items.map(x => ({
                id: x.id,
                hoSoTuyenSinhId: x.hoSoTuyenSinhId,
                diemLyThuyet: x.diemLyThuyet !== '' ? parseFloat(x.diemLyThuyet) : null,
                diemThucHanh: x.diemThucHanh !== '' ? parseFloat(x.diemThucHanh) : null,
                diemTrungBinh: x.diemTrungBinh !== '' ? parseFloat(x.diemTrungBinh) : null,
                xepLoai: x.xepLoai || null,
                isDat: x.isDat,
                nhanXet: x.nhanXet || null
            }))
        };
        saveMutation.mutate(payload);
    };

    // Xuất Excel bảng điểm lớp
    const handleExportExcel = () => {
        if (items.length === 0) {
            toast.warning('Chưa có dữ liệu bảng điểm để xuất!');
            return;
        }

        try {
            let csvContent = "\ufeff";
            csvContent += "TT,Họ và tên,Ngày sinh,Điểm lý thuyết,Điểm thực hành,Điểm trung bình,Xếp loại,Kết quả,Nhận xét\n";

            items.forEach((item, idx) => {
                const hoTen = `"${(item.hoTen || '').replace(/"/g, '""')}"`;
                const ngaySinh = item.ngaySinh ? moment(item.ngaySinh).format('DD/MM/YYYY') : '';
                const diemLT = item.diemLyThuyet !== '' && item.diemLyThuyet !== null ? item.diemLyThuyet : '';
                const diemTH = item.diemThucHanh !== '' && item.diemThucHanh !== null ? item.diemThucHanh : '';
                const diemTB = item.diemTrungBinh !== '' && item.diemTrungBinh !== null ? item.diemTrungBinh : '';
                const xepLoai = `"${(item.xepLoai || '').replace(/"/g, '""')}"`;
                const ketQua = item.isDat === true ? 'Đạt' : item.isDat === false ? 'Không đạt' : 'Chưa đánh giá';
                const nhanXet = `"${(item.nhanXet || '').replace(/"/g, '""')}"`;

                csvContent += `${idx + 1},${hoTen},${ngaySinh},${diemLT},${diemTH},${diemTB},${xepLoai},${ketQua},${nhanXet}\n`;
            });

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            const fileName = `BangDiem_${lopDetail?.tenKhoaHoc ? lopDetail.tenKhoaHoc.replace(/[^a-zA-Z0-9]/g, '_') : 'Lop'}_${moment().format('YYYYMMDD')}.csv`;
            link.setAttribute("href", url);
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Xuất file bảng điểm thành công!');
        } catch (error) {
            toast.error('Lỗi khi xuất file bảng điểm: ' + error.message);
        }
    };

    // Filter tìm kiếm theo Họ tên / CCCD
    const filteredItems = items.filter(item => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase().trim();
        return (
            (item.hoTen && item.hoTen.toLowerCase().includes(term)) ||
            (item.soCccd && item.soCccd.includes(term))
        );
    });

    // Thống kê nhanh
    const totalCount = items.length;
    const datCount = items.filter(x => x.isDat === true).length;
    const khongDatCount = items.filter(x => x.isDat === false).length;
    const chuaDanhGiaCount = items.filter(x => x.isDat === null || x.isDat === undefined).length;

    // Lấy initials cho avatar học viên mobile
    const getInitials = (name) => {
        if (!name) return 'HV';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const currentMobileItem = filteredItems[mobileIndex] || null;

    return (
        <div>
            {/* ========================================================================= */}
            {/* 1. DESKTOP VIEW (>= 768px): BẢNG SPREADSHEET ĐẦY ĐỦ                          */}
            {/* ========================================================================= */}
            <div className="d-none d-md-block">
                {/* 1.1 KHU VỰC UPLOAD FILE MINH CHỨNG DUY NHẤT Ở ĐẦU TRANG */}
                <Card className="shadow-sm border-0 mb-3" style={{ borderLeft: '4px solid #2563eb' }}>
                    <Card.Body className="py-3">
                        <Row className="align-items-center">
                            <Col md={7}>
                                <h6 className="text-primary font-weight-bold mb-1">
                                    <i className="fas fa-file-invoice mr-2"></i>
                                    File Minh Chứng Bảng Điểm Của Khoa Gửi
                                </h6>
                                <div className="text-muted small">
                                    {fileMinhChungPath ? (
                                        <div className="d-flex align-items-center mt-2">
                                            <i className="fas fa-file-word text-primary mr-2" style={{ fontSize: '20px' }}></i>
                                            <span className="font-weight-bold mr-3">{fileMinhChungName || 'File_BangDiem.docx'}</span>
                                            <a
                                                href={`${BaseGatewayImage}/${fileMinhChungPath}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-outline-info mr-2"
                                            >
                                                <i className="fas fa-download mr-1"></i> Tải về xem
                                            </a>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={handleRemoveFile}
                                            >
                                                <i className="fas fa-trash-alt mr-1"></i> Xóa file
                                            </Button>
                                        </div>
                                    ) : (
                                        <span className="text-muted">Chưa có file minh chứng nào được đính kèm.</span>
                                    )}
                                </div>
                            </Col>
                            <Col md={5} className="text-md-right mt-2 mt-md-0">
                                <label className="btn btn-sm btn-outline-primary mb-0 cursor-pointer">
                                    <i className="fas fa-cloud-upload-alt mr-1"></i>
                                    {isUploadingFile ? 'Đang tải lên...' : fileMinhChungPath ? 'Thay đổi file khác' : 'Tải lên file bảng điểm'}
                                    <input
                                        type="file"
                                        hidden
                                        accept=".docx, .doc, .pdf, .xlsx, .xls, .png, .jpg, .jpeg"
                                        onChange={handleFileUpload}
                                        disabled={isUploadingFile}
                                    />
                                </label>
                                {isUploadingFile && <Spinner animation="border" size="sm" className="ml-2" />}
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* 1.2 THANH CÔNG CỤ & THỐNG KÊ TỔNG QUAN */}
                <Card className="shadow-sm border-0 mb-3">
                    <Card.Header className="bg-white py-3">
                        <Row className="align-items-center justify-content-between">
                            <Col lg={5} md={12} className="mb-2 mb-lg-0">
                                <div className="d-flex flex-wrap align-items-center">
                                    <Badge variant="primary" className="p-2 mr-2 mb-1" style={{ fontSize: '13px' }}>
                                        Tổng số: <strong>{totalCount}</strong> HV
                                    </Badge>
                                    <Badge variant="success" className="p-2 mr-2 mb-1" style={{ fontSize: '13px' }}>
                                        Đạt: <strong>{datCount}</strong> ({totalCount > 0 ? Math.round((datCount / totalCount) * 100) : 0}%)
                                    </Badge>
                                    <Badge variant="danger" className="p-2 mr-2 mb-1" style={{ fontSize: '13px' }}>
                                        Không đạt: <strong>{khongDatCount}</strong>
                                    </Badge>
                                    {chuaDanhGiaCount > 0 && (
                                        <Badge variant="secondary" className="p-2 mb-1" style={{ fontSize: '13px' }}>
                                            Chưa đánh giá: <strong>{chuaDanhGiaCount}</strong>
                                        </Badge>
                                    )}
                                </div>
                            </Col>

                            <Col lg={7} md={12} className="text-lg-right">
                                <div className="d-flex flex-wrap justify-content-lg-end align-items-center">
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder="Tìm tên, CCCD..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ width: '180px' }}
                                        className="mr-2 mb-1"
                                    />

                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="mr-2 mb-1"
                                        onClick={() => syncMutation.mutate()}
                                        disabled={syncMutation.isLoading}
                                    >
                                        <i className={`fas fa-sync mr-1 ${syncMutation.isLoading ? 'fa-spin' : ''}`}></i>
                                        Đồng bộ DS
                                    </Button>

                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        className="mr-2 mb-1"
                                        onClick={() => setShowImportModal(true)}
                                    >
                                        <i className="fas fa-file-excel mr-1"></i>
                                        Nhập từ Excel
                                    </Button>

                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        className="mr-2 mb-1"
                                        onClick={handleExportExcel}
                                        disabled={items.length === 0}
                                    >
                                        <i className="fas fa-download mr-1"></i>
                                        Xuất bảng điểm
                                    </Button>

                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="mb-1 font-weight-bold"
                                        onClick={handleSaveAll}
                                        disabled={saveMutation.isLoading || items.length === 0}
                                    >
                                        <i className="fas fa-save mr-1"></i>
                                        {saveMutation.isLoading ? 'Đang lưu...' : 'Lưu bảng điểm'}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Card.Header>

                    {/* 1.3 BẢNG NHẬP ĐIỂM SPREADSHEET */}
                    <Card.Body className="p-0">
                        {isLoading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="mt-2 text-muted">Đang tải danh sách bảng điểm...</div>
                            </div>
                        ) : items.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="fas fa-clipboard-list text-muted mb-3" style={{ fontSize: '42px' }}></i>
                                <h5>Chưa có dữ liệu bảng điểm cho lớp này.</h5>
                                <p className="text-muted">Bấm nút "Đồng bộ DS" để tự động nạp danh sách học viên của lớp vào bảng điểm.</p>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => syncMutation.mutate()}
                                    disabled={syncMutation.isLoading}
                                >
                                    <i className="fas fa-sync mr-1"></i> Khởi tạo bảng điểm ngay
                                </Button>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover bordered className="mb-0 text-nowrap align-middle">
                                    <thead className="thead-light">
                                        <tr className="text-center text-secondary">
                                            <th style={{ width: '40px' }}>TT</th>
                                            <th style={{ minWidth: '180px', textAlign: 'left' }}>Họ và tên</th>
                                            <th style={{ width: '100px' }}>Ngày sinh</th>
                                            <th style={{ width: '110px' }}>Điểm lý thuyết</th>
                                            <th style={{ width: '110px' }}>Điểm thực hành</th>
                                            <th style={{ width: '120px' }}>Điểm trung bình</th>
                                            <th style={{ width: '140px' }}>Xếp loại</th>
                                            <th style={{ width: '140px' }}>Kết quả</th>
                                            <th style={{ minWidth: '180px' }}>Nhận xét</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredItems.map((item, idx) => {
                                            const originalIdx = items.findIndex(x => x.hoSoTuyenSinhId === item.hoSoTuyenSinhId);
                                            return (
                                                <tr key={item.hoSoTuyenSinhId || idx}>
                                                    <td className="text-center font-weight-bold text-muted">{idx + 1}</td>
                                                    <td className="font-weight-bold text-dark">
                                                        {item.hoTen}
                                                        {item.soCccd && <div className="text-muted small" style={{ fontSize: '11px' }}>CCCD: {item.soCccd}</div>}
                                                    </td>
                                                    <td className="text-center small text-muted">
                                                        {item.ngaySinh ? moment(item.ngaySinh).format('DD/MM/YYYY') : '—'}
                                                    </td>

                                                    {/* Điểm Lý thuyết */}
                                                    <td className="p-1">
                                                        <Form.Control
                                                            size="sm"
                                                            type="number"
                                                            step="0.1"
                                                            min="0"
                                                            max="10"
                                                            className="text-center font-weight-bold"
                                                            value={item.diemLyThuyet}
                                                            placeholder="—"
                                                            onChange={(e) => handleFieldChange(originalIdx, 'diemLyThuyet', e.target.value)}
                                                        />
                                                    </td>

                                                    {/* Điểm Thực hành */}
                                                    <td className="p-1">
                                                        <Form.Control
                                                            size="sm"
                                                            type="number"
                                                            step="0.1"
                                                            min="0"
                                                            max="10"
                                                            className="text-center font-weight-bold"
                                                            value={item.diemThucHanh}
                                                            placeholder="—"
                                                            onChange={(e) => handleFieldChange(originalIdx, 'diemThucHanh', e.target.value)}
                                                        />
                                                    </td>

                                                    {/* Điểm Trung bình */}
                                                    <td className="p-1">
                                                        <div className="d-flex align-items-center">
                                                            <Form.Control
                                                                size="sm"
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max="10"
                                                                className="text-center font-weight-bold text-primary"
                                                                value={item.diemTrungBinh}
                                                                placeholder="—"
                                                                onChange={(e) => handleFieldChange(originalIdx, 'diemTrungBinh', e.target.value)}
                                                            />
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="p-0 ml-1 text-muted"
                                                                title="Gợi ý tính tự động (LT + TH) / 2"
                                                                onClick={() => handleAutoCalculateRow(originalIdx)}
                                                            >
                                                                <i className="fas fa-calculator"></i>
                                                            </Button>
                                                        </div>
                                                    </td>

                                                    {/* Xếp loại */}
                                                    <td className="p-1">
                                                        <Form.Control
                                                            as="select"
                                                            size="sm"
                                                            value={item.xepLoai || ''}
                                                            onChange={(e) => handleFieldChange(originalIdx, 'xepLoai', e.target.value)}
                                                        >
                                                            {XEP_LOAI_OPTIONS.map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </Form.Control>
                                                    </td>

                                                    {/* Kết quả Đạt / Không đạt */}
                                                    <td className="p-1 text-center">
                                                        <Form.Control
                                                            as="select"
                                                            size="sm"
                                                            value={item.isDat === true ? '1' : item.isDat === false ? '0' : ''}
                                                            onChange={(e) => {
                                                                const val = e.target.value === '1' ? true : e.target.value === '0' ? false : null;
                                                                handleFieldChange(originalIdx, 'isDat', val);
                                                            }}
                                                            className={
                                                                item.isDat === true ? 'text-success font-weight-bold' :
                                                                item.isDat === false ? 'text-danger font-weight-bold' : ''
                                                            }
                                                        >
                                                            <option value="">-- Chưa đánh giá --</option>
                                                            <option value="1">Đạt</option>
                                                            <option value="0">Không đạt</option>
                                                        </Form.Control>
                                                    </td>

                                                    {/* Nhận xét */}
                                                    <td className="p-1">
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            placeholder="Nhận xét riêng..."
                                                            value={item.nhanXet || ''}
                                                            onChange={(e) => handleFieldChange(originalIdx, 'nhanXet', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </div>

            {/* ========================================================================= */}
            {/* 2. MOBILE VIEW (< 768px): STEPPER CARD CHUẨN MOCKUP                       */}
            {/* ========================================================================= */}
            <div className="d-block d-md-none">
                {/* 2.1 File Minh Chứng Card */}
                <div className="bg-white rounded-lg p-3 mb-2 border shadow-sm" style={{ borderRadius: '12px' }}>
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <div
                                className="d-flex align-items-center justify-content-center mr-2 font-weight-bold text-primary"
                                style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#e0f2fe', fontSize: '16px' }}
                            >
                                W
                            </div>
                            <div>
                                <div className="font-weight-bold text-dark" style={{ fontSize: '13px' }}>
                                    {fileMinhChungName || 'Chưa có file minh chứng'}
                                </div>
                                <div className="text-muted small" style={{ fontSize: '11px' }}>
                                    {fileMinhChungPath ? 'Đã tải lên' : 'Chạm để tải file lên'}
                                </div>
                            </div>
                        </div>
                        <div>
                            {fileMinhChungPath ? (
                                <div className="d-flex align-items-center">
                                    <a
                                        href={`${BaseGatewayImage}/${fileMinhChungPath}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-primary py-1 px-2 mr-1"
                                        style={{ fontSize: '12px' }}
                                    >
                                        Xem
                                    </a>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="py-1 px-2"
                                        style={{ fontSize: '12px' }}
                                        onClick={handleRemoveFile}
                                    >
                                        ✕
                                    </Button>
                                </div>
                            ) : (
                                <label className="btn btn-sm btn-primary mb-0 py-1 px-2" style={{ fontSize: '12px' }}>
                                    Tải lên
                                    <input
                                        type="file"
                                        hidden
                                        accept=".docx, .doc, .pdf, .xlsx, .xls, .png, .jpg, .jpeg"
                                        onChange={handleFileUpload}
                                        disabled={isUploadingFile}
                                    />
                                </label>
                            )}
                        </div>
                    </div>
                </div>

                {/* 2.2 Ba Thẻ Thống Kê Nhanh */}
                <Row className="g-2 mb-2">
                    <Col xs={4}>
                        <div className="bg-white p-2 border rounded shadow-sm text-center" style={{ borderRadius: '10px' }}>
                            <div className="font-weight-bold text-primary" style={{ fontSize: '18px' }}>{totalCount}</div>
                            <div className="text-muted" style={{ fontSize: '11px' }}>Học viên</div>
                        </div>
                    </Col>
                    <Col xs={4}>
                        <div className="bg-white p-2 border rounded shadow-sm text-center" style={{ borderRadius: '10px' }}>
                            <div className="font-weight-bold text-success" style={{ fontSize: '18px' }}>{datCount}</div>
                            <div className="text-muted" style={{ fontSize: '11px' }}>Đạt</div>
                        </div>
                    </Col>
                    <Col xs={4}>
                        <div className="bg-white p-2 border rounded shadow-sm text-center" style={{ borderRadius: '10px' }}>
                            <div className="font-weight-bold text-danger" style={{ fontSize: '18px' }}>{khongDatCount}</div>
                            <div className="text-muted" style={{ fontSize: '11px' }}>Không đạt</div>
                        </div>
                    </Col>
                </Row>

                {/* 2.3 Toolbar: Search & Nút Action */}
                <div className="bg-white p-2 border rounded shadow-sm mb-2" style={{ borderRadius: '10px' }}>
                    <Form.Control
                        size="sm"
                        type="text"
                        placeholder="Tìm học viên..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setMobileIndex(0);
                        }}
                        className="mb-2"
                        style={{ fontSize: '13px' }}
                    />
                    <div className="d-flex justify-content-between">
                        <Button
                            variant="outline-info"
                            size="sm"
                            className="py-1 px-3"
                            style={{ fontSize: '12px' }}
                            onClick={() => setShowImportModal(true)}
                        >
                            <i className="fas fa-file-excel mr-1"></i> Excel
                        </Button>
                        <Button
                            variant="outline-success"
                            size="sm"
                            className="py-1 px-3"
                            style={{ fontSize: '12px' }}
                            onClick={handleExportExcel}
                            disabled={items.length === 0}
                        >
                            <i className="fas fa-download mr-1"></i> Xuất
                        </Button>
                    </div>
                </div>

                {/* 2.4 Điều hướng Chuyển Học Viên Stepper */}
                {filteredItems.length > 0 && (
                    <div className="d-flex align-items-center justify-content-between bg-white px-3 py-2 border rounded shadow-sm mb-2" style={{ borderRadius: '10px' }}>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={mobileIndex <= 0}
                            onClick={() => setMobileIndex(prev => Math.max(0, prev - 1))}
                            style={{ fontSize: '12px' }}
                        >
                            ‹ Trước
                        </Button>
                        <span className="font-weight-bold text-muted small">
                            Học viên {mobileIndex + 1} / {filteredItems.length}
                        </span>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={mobileIndex >= filteredItems.length - 1}
                            onClick={() => setMobileIndex(prev => Math.min(filteredItems.length - 1, prev + 1))}
                            style={{ fontSize: '12px' }}
                        >
                            Sau ›
                        </Button>
                    </div>
                )}

                {/* 2.5 Card Chi Tiết Học Viên */}
                {currentMobileItem ? (
                    (() => {
                        const originalIdx = items.findIndex(x => x.hoSoTuyenSinhId === currentMobileItem.hoSoTuyenSinhId);
                        return (
                            <div className="bg-white p-3 border rounded-lg shadow-sm mb-3" style={{ borderRadius: '14px' }}>
                                {/* Header học viên */}
                                <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="d-flex align-items-center justify-content-center mr-2 font-weight-bold text-primary"
                                            style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#e0f2fe', fontSize: '15px' }}
                                        >
                                            {getInitials(currentMobileItem.hoTen)}
                                        </div>
                                        <div>
                                            <div className="font-weight-bold text-dark" style={{ fontSize: '15px' }}>
                                                {currentMobileItem.hoTen}
                                            </div>
                                            <div className="text-muted small" style={{ fontSize: '11px' }}>
                                                {currentMobileItem.soCccd ? `CCCD: ${currentMobileItem.soCccd} · ` : ''}
                                                {currentMobileItem.ngaySinh ? moment(currentMobileItem.ngaySinh).format('DD/MM/YYYY') : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        {currentMobileItem.isDat === true && (
                                            <span className="badge px-2 py-1" style={{ backgroundColor: '#dcfce7', color: '#15803d', fontSize: '12px' }}>
                                                Đạt
                                            </span>
                                        )}
                                        {currentMobileItem.isDat === false && (
                                            <span className="badge px-2 py-1" style={{ backgroundColor: '#fee2e2', color: '#b91c1c', fontSize: '12px' }}>
                                                Không đạt
                                            </span>
                                        )}
                                        {currentMobileItem.isDat === null && (
                                            <span className="badge px-2 py-1" style={{ backgroundColor: '#f1f5f9', color: '#64748b', fontSize: '12px' }}>
                                                Chưa xét
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Form nhập điểm */}
                                <Row className="mb-2">
                                    <Col xs={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="text-muted small mb-1" style={{ fontSize: '12px' }}>Điểm lý thuyết /10</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="10"
                                                className="text-center font-weight-bold"
                                                value={currentMobileItem.diemLyThuyet}
                                                placeholder="—"
                                                onChange={(e) => handleFieldChange(originalIdx, 'diemLyThuyet', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="text-muted small mb-1" style={{ fontSize: '12px' }}>Điểm thực hành /10</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="10"
                                                className="text-center font-weight-bold"
                                                value={currentMobileItem.diemThucHanh}
                                                placeholder="—"
                                                onChange={(e) => handleFieldChange(originalIdx, 'diemThucHanh', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-2">
                                    <Form.Label className="text-muted small mb-1" style={{ fontSize: '12px' }}>Xếp loại</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={currentMobileItem.xepLoai || ''}
                                        onChange={(e) => handleFieldChange(originalIdx, 'xepLoai', e.target.value)}
                                    >
                                        {XEP_LOAI_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>

                                {/* Ô Điểm Trung Bình & Kết Quả (TỰ ĐIỀN / TỰ CHỌN THEO YÊU CẦU ĐẠI CA) */}
                                <Row className="mb-2">
                                    <Col xs={6}>
                                        <div className="p-2 border rounded" style={{ backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }}>
                                            <Form.Label className="text-muted small mb-1 d-flex justify-content-between" style={{ fontSize: '11px' }}>
                                                <span>Điểm trung bình</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 text-primary border-0"
                                                    style={{ fontSize: '11px' }}
                                                    onClick={() => handleAutoCalculateRow(originalIdx)}
                                                >
                                                    Tính
                                                </button>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="10"
                                                className="text-center font-weight-bold text-primary"
                                                style={{ fontSize: '16px', height: '36px' }}
                                                value={currentMobileItem.diemTrungBinh}
                                                placeholder="—"
                                                onChange={(e) => handleFieldChange(originalIdx, 'diemTrungBinh', e.target.value)}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="p-2 border rounded" style={{ backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }}>
                                            <Form.Label className="text-muted small mb-1" style={{ fontSize: '11px' }}>Kết quả cuối khóa</Form.Label>
                                            <Form.Control
                                                as="select"
                                                style={{ fontSize: '13px', height: '36px' }}
                                                value={currentMobileItem.isDat === true ? '1' : currentMobileItem.isDat === false ? '0' : ''}
                                                onChange={(e) => {
                                                    const val = e.target.value === '1' ? true : e.target.value === '0' ? false : null;
                                                    handleFieldChange(originalIdx, 'isDat', val);
                                                }}
                                                className={
                                                    currentMobileItem.isDat === true ? 'text-success font-weight-bold' :
                                                    currentMobileItem.isDat === false ? 'text-danger font-weight-bold' : ''
                                                }
                                            >
                                                <option value="">Chưa xét</option>
                                                <option value="1">Đạt</option>
                                                <option value="0">Không đạt</option>
                                            </Form.Control>
                                        </div>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted small mb-1" style={{ fontSize: '12px' }}>Nhận xét</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Nhập nhận xét cho học viên..."
                                        value={currentMobileItem.nhanXet || ''}
                                        onChange={(e) => handleFieldChange(originalIdx, 'nhanXet', e.target.value)}
                                    />
                                </Form.Group>

                                {/* Action Buttons Dưới Cùng */}
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        className="w-25 py-2"
                                        style={{ fontSize: '13px' }}
                                        onClick={() => refetch()}
                                    >
                                        Hủy
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="w-50 py-2 font-weight-bold"
                                        style={{ fontSize: '13px' }}
                                        onClick={handleSaveAll}
                                        disabled={saveMutation.isLoading}
                                    >
                                        <i className="fas fa-save mr-1"></i> Lưu lớp
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="w-50 py-2 font-weight-bold"
                                        style={{ fontSize: '13px', backgroundColor: '#2563eb' }}
                                        onClick={() => {
                                            handleSaveAll();
                                            if (mobileIndex < filteredItems.length - 1) {
                                                setMobileIndex(prev => prev + 1);
                                            }
                                        }}
                                        disabled={saveMutation.isLoading}
                                    >
                                        Lưu & tiếp ›
                                    </Button>
                                </div>
                            </div>
                        );
                    })()
                ) : (
                    <div className="text-center py-4 bg-white rounded border">
                        <div className="text-muted mb-2">Chưa có học viên nào.</div>
                        <Button size="sm" variant="primary" onClick={() => syncMutation.mutate()}>
                            Đồng bộ danh sách
                        </Button>
                    </div>
                )}
            </div>

            {/* 3. MODAL IMPORT EXCEL */}
            <ImportBangDiemLienTucModal
                show={showImportModal}
                onHide={() => setShowImportModal(false)}
                lopId={lopId}
                onApply={handleApplyImportedData}
            />
        </div>
    );
};

export default BangDiemLienTucTab;
